import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from '@vue/composition-api';
import Icon from 'components/Icon.vue';
import { BfButton } from 'components/Butterfly';
import { useStore } from 'src/store';
import ToggleCheckbox from 'components/widgets/ToggleCheckbox.vue';
import Utils from 'src/utils/general';
const IconMap = {
    'NUMBER': 'hashtag',
    'LABEL': 'bar-chart',
    'NPS': 'person-heart',
    'SENTIMENT': 'smile',
    'CONCEPT': 'chat-filled',
    'THEME': 'chat-filled',
    'OPERATOR': 'gear',
    'DATE': 'calendar',
};
const SegmentMenu = defineComponent({
    components: {
        Icon,
        BfButton,
        ToggleCheckbox,
    },
    props: {
        hiddenTypes: { type: Array, default: () => [] },
        queryRows: { type: Array, default: () => [] },
        conceptNames: { type: Array, default: () => [] },
        themes: { type: Array, default: () => [] },
        isActive: { type: Boolean, default: true },
        multiselect: { type: Boolean, default: true },
        allowStructured: { type: Boolean, default: true },
        allowText: { type: Boolean, default: true },
        allowOperators: { type: Boolean, default: true },
        allowThemes: { type: Boolean, default: true },
        allowConcepts: { type: Boolean, default: true },
        allowDates: { type: Boolean, default: true },
    },
    setup(props, { emit }) {
        const store = useStore();
        const searchQ = ref('');
        const ctrlDown = ref(false);
        const onCtrlDown = (e) => {
            if (e.metaKey || e.ctrlKey) {
                ctrlDown.value = true;
            }
        };
        const onCtrlUp = (e) => {
            if (!e.metaKey && !e.ctrlKey) {
                ctrlDown.value = false;
            }
        };
        onMounted(() => {
            if (props.multiselect) {
                window.addEventListener('keydown', onCtrlDown);
                window.addEventListener('keyup', onCtrlUp);
            }
        });
        onBeforeUnmount(() => {
            window.removeEventListener('keydown', onCtrlDown);
            window.removeEventListener('keyup', onCtrlUp);
        });
        const multiselectActive = computed(() => {
            return props.multiselect && (ctrlDown.value || filters.value.length > 0);
        });
        watch(searchQ, (newVal, oldVal) => {
            if (newVal !== oldVal) {
                hoveredField.value = null;
            }
        });
        watch(() => props.isActive, (newVal, oldVal) => {
            if (!newVal && newVal !== oldVal) {
                hoveredField.value = null;
            }
        });
        const sortedSegmentsForFieldsUnlimited = computed(() => store.getters['sortedSegmentsForFieldsUnlimited']);
        const sortedFieldsUnlimited = computed(() => store.getters['sortedFieldsUnlimited']);
        const dateFields = computed(() => store.getters['dateFields']);
        const fieldList = computed(() => {
            const { hiddenTypes } = props;
            const allowedFields = props.allowStructured
                ? sortedFieldsUnlimited.value.filter((field) => !hiddenTypes.includes(field.type))
                : [];
            const fields = allowedFields.map((field) => {
                const fieldItem = {
                    segments: sortedSegmentsForFieldsUnlimited.value[field.name] ?? [],
                    name: field.name,
                    type: field.typename,
                };
                if (field.name === 'sentiment') {
                    fieldItem.segments = ['positive', 'negative', 'neutral', 'mixed'];
                    fieldItem.type = 'SENTIMENT';
                }
                return fieldItem;
            });
            if (props.allowDates && dateFields.value.length > 0) {
                fields.unshift({
                    segments: dateFields.value.map((field) => field.name),
                    name: 'Date Fields',
                    type: 'DATE',
                });
            }
            if (props.allowOperators) {
                fields.unshift({
                    segments: ['Word Count'],
                    name: 'Operators',
                    type: 'OPERATOR',
                });
            }
            if (props.allowThemes && props.themes?.length) {
                fields.unshift({
                    segments: props.themes.map((theme) => theme.name),
                    name: 'Themes',
                    type: 'THEME',
                });
            }
            if (props.allowConcepts && props.conceptNames?.length) {
                fields.unshift({
                    segments: props.conceptNames,
                    name: 'Concepts',
                    type: 'CONCEPT',
                });
            }
            if (searchQ.value) {
                fields.forEach((field) => {
                    field.segments = field.segments.filter((segment) => {
                        return segment.toLowerCase().includes(searchQ.value.toLowerCase());
                    });
                });
            }
            return fields;
        });
        const hoveredField = ref(null);
        const filters = ref([]);
        const filtersContain = (field, segment) => {
            return filters.value.some(([f, s]) => f.name === field && s === segment);
        };
        const addFilter = (field, segment) => {
            if (multiselectActive.value) {
                if (filtersContain(field.name, segment)) {
                    filters.value = filters.value.filter(([f, s]) => f.name !== field.name || s !== segment);
                }
                else {
                    filters.value.push([field, segment]);
                }
            }
            else {
                filters.value = [[field, segment]];
                submitFilters();
            }
        };
        const submitFilters = () => {
            let conceptRow = null;
            let queryRow = null;
            let sentimentRow = null;
            let wordCountRow = null;
            let segmentRows = [];
            filters.value.forEach(([field, segment]) => {
                if (field.type === 'CONCEPT') {
                    conceptRow ||= {
                        type: 'text',
                        operator: 'includes',
                        values: [],
                    };
                    conceptRow.values.push(segment);
                }
                else if (field.type === 'THEME') {
                    const queryId = props.themes.find((t) => t.name === segment)?.id;
                    if (queryId == null)
                        return;
                    queryRow ||= {
                        type: 'query',
                        operator: 'includes',
                        values: [],
                    };
                    queryRow.values.push(queryId.toString());
                }
                else if (field.type === 'SENTIMENT') {
                    sentimentRow ||= {
                        type: 'attribute',
                        operator: 'is',
                        values: [],
                        field: field.name,
                    };
                    sentimentRow.values.push(segment);
                }
                else if (field.type === 'OPERATOR') {
                    if (segment === 'Word Count') {
                        wordCountRow = {
                            type: 'segment',
                            operator: 'is greater than',
                            values: [],
                            field: 'Token Count',
                        };
                    }
                }
                else if (field.type === 'DATE') {
                    segmentRows.push({
                        field: segment,
                        type: 'segment',
                        operator: 'is',
                        is_date: true,
                        values: [],
                    });
                }
                else {
                    const segmentRow = segmentRows.find((row) => row.field === field.name);
                    if (segmentRow) {
                        segmentRow.values.push(segment);
                    }
                    else {
                        segmentRows.push({
                            field: field.name,
                            type: 'segment',
                            operator: 'is',
                            values: [segment],
                        });
                    }
                }
            });
            filters.value = [];
            conceptRow && emit('add-filter', conceptRow);
            queryRow && emit('add-filter', queryRow);
            sentimentRow && emit('add-filter', sentimentRow);
            wordCountRow && emit('add-filter', wordCountRow);
            for (const segmentRow of segmentRows) {
                emit('add-filter', segmentRow);
            }
        };
        // Check if queryRows contains a field segment
        const hasSegment = (field, segment) => {
            const filter = props.queryRows.reduce((arr, row) => {
                return row.field === field ||
                    (field === 'Concepts' && row.type === 'text')
                    ? arr.concat(row.values)
                    : arr;
            }, []);
            return filter.includes(segment);
        };
        const clearQ = () => searchQ.value = '';
        const addText = () => {
            if (!searchQ.value || !props.allowText)
                return;
            let value = Utils.sanitisePhraseQuery(searchQ.value);
            if (value === "") {
                return;
            }
            const filter = {
                type: 'text',
                operator: 'includes',
                values: [value],
            };
            searchQ.value = '';
            emit('add-filter', filter);
        };
        const emptyResults = computed(() => {
            return fieldList.value.every((field) => field.segments.length === 0);
        });
        const displaySegments = computed(() => {
            return hoveredField.value?.segments ?? [];
        });
        const displaySegmentsLimited = computed(() => {
            const displayLimit = 100;
            return displaySegments.value.slice(0, displayLimit);
        });
        const hitDisplayLimit = computed(() => {
            if (!hoveredField.value)
                return false;
            return displaySegmentsLimited.value.length < displaySegments.value.length;
        });
        return {
            fieldList,
            hoveredField,
            addFilter,
            IconMap,
            hasSegment,
            searchQ,
            clearQ,
            displaySegments,
            hitDisplayLimit,
            displaySegmentsLimited,
            emptyResults,
            addText,
            filters,
            submitFilters,
            ctrlDown,
            multiselectActive,
            filtersContain,
        };
    },
});
export default SegmentMenu;
